import { Injectable, inject } from '@angular/core';
import {
  CmUiConfirmComponent,
  CmUiDialogService,
  CmUiFileImportModalComponent,
  CmUiSnackBarService,
} from '@cm/ui-modules';
import { FrontendTranslateService } from '@shared/services/translation.service';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ModalTranslationKeys } from '../models';
import { ObjectName } from '@view-model';

@Injectable({ providedIn: 'root' })
export class DataListService {
  private snackBar = inject(CmUiSnackBarService);
  private translate = inject(FrontendTranslateService);
  private dialog = inject(CmUiDialogService);

  showNotification(translationKey: string): void {
    this.snackBar.showNotification(this.translate.instant(translationKey));
  }

  showConfirmDialog(translationKeys: ModalTranslationKeys): Observable<boolean> {
    // `smallPanel` dropped — its rule (src/styles.scss) is scoped entirely to
    // `mat-dialog-container`-nested selectors, dead under UiDialogContainerComponent (no such
    // element exists there); see docs/ui-modules.md § cm-ui-dialog.
    return this.dialog
      .open<CmUiConfirmComponent, unknown, unknown>(CmUiConfirmComponent, {
        data: {
          title: this.translate.instant(translationKeys.title),
          message: this.translate.instant(translationKeys.message),
          confirmButtonAlert: true,
          confirmLabel: this.translate.instant(translationKeys.confirm),
          cancelLabel: this.translate.instant(translationKeys.cancel),
        },
      })
      .closed.pipe(
        filter((result) => this.isConfirmed(result)),
        map(() => true),
      );
  }

  setPaginationToSession(pageSize: number, pageIndex: number, objectName: ObjectName): void {
    localStorage.setItem(`${objectName}_pageSize`, `${pageSize}`);
    localStorage.setItem(`${objectName}_pageIndex`, `${pageIndex}`);
  }

  showImportFileDialog(options?: {
    showMoveMediaAssets?: boolean;
    moveMediaAssetsLabel?: string;
  }): Observable<{ status: 'error' | 'success' | 'cancel'; formData?: FormData; error?: any }> {
    return this.dialog
      .open<CmUiFileImportModalComponent, unknown, any>(
        CmUiFileImportModalComponent,
        {
          data: {
            confirmLabel: this.translate.instant('COMMON.IMPORT'),
            cancelLabel: this.translate.instant('COMMON.CANCEL'),
            showMoveMediaAssets: options?.showMoveMediaAssets ?? false,
            moveMediaAssetsLabel: options?.moveMediaAssetsLabel,
          },
        },
      )
      .closed.pipe(filter((confirmFlag) => !!confirmFlag));
  }

  private isConfirmed(result: unknown): boolean {
    if (result === true) {
      return true;
    }

    return (
      !!result &&
      typeof result === 'object' &&
      (result as { confirmed?: boolean }).confirmed === true
    );
  }
}
